import { useEffect } from 'react';
import {
  ArrowLeft,
  ArrowRight,
  Bot,
  Check,
  Lightbulb,
  Network,
  Search,
  ShieldCheck,
  Smartphone,
  Sparkles,
  Workflow,
} from 'lucide-react';
import {
  AutomationVisual,
  DigitizationVisual,
  ProductDevelopmentVisual,
} from './components/EditorialVisuals';
import { Navigation } from './components/Navigation';
import { SiteFooter } from './components/SiteFooter';
import { siteConfig } from './siteConfig';

type Locale = 'sv' | 'en';
type PageKey = 'app' | 'digitalization' | 'ai' | 'about';

type LandingCopy = {
  eyebrow: string;
  title: string;
  intro: string;
  home: string;
  contact: string;
  language: string;
  situationsTitle: string;
  situationsIntro: string;
  situations: Array<[string, string]>;
  offerTitle: string;
  offerIntro: string;
  offer: Array<[string, string]>;
  processTitle: string;
  processIntro: string;
  process: Array<[string, string]>;
  principlesTitle: string;
  principles: string[];
  faqTitle: string;
  faq: Array<[string, string]>;
  ctaTitle: string;
  ctaBody: string;
  ctaButton: string;
  direct: string;
};

type PageConfig = {
  key: PageKey;
  svPath: string;
  enPath: string;
  copy: Record<Locale, LandingCopy>;
};

const pageConfigs: PageConfig[] = [
  {
    key: 'app',
    svPath: '/apputveckling/',
    enPath: '/en/app-development/',
    copy: {
      sv: {
        eyebrow: 'Produktutveckling · Apputveckling',
        title: 'Från appidé till en produkt som går att använda.',
        intro:
          'Keevo hjälper till att strukturera, designa och utveckla mobilappar och webbappar. Projektet kan börja med en idé, ett tydligt problem eller ett befintligt koncept som behöver bli konkret.',
        home: 'Till startsidan',
        contact: 'Starta en dialog',
        language: 'English',
        situationsTitle: 'När apputveckling är rätt nästa steg',
        situationsIntro:
          'Vi börjar inte med teknikvalet. Först behöver vi förstå vad produkten ska göra, för vem och vilket problem den ska lösa.',
        situations: [
          ['Du har en appidé men ingen färdig kravspecifikation', 'Vi hjälper till att avgränsa behov, användare, funktioner och vad som behöver testas först.'],
          ['Ett manuellt arbetssätt borde kunna bli en digital produkt', 'Vi går från verksamhetsproblem till användarflöden och en lösning som kan byggas och prövas.'],
          ['Du behöver en prototyp eller MVP för att komma vidare', 'Vi prioriterar det som behöver fungera först och undviker att bygga omfattning utan tydligt syfte.'],
        ],
        offerTitle: 'Vad Keevo kan hjälpa till med',
        offerIntro:
          'Omfattningen anpassas efter projektet. Målet är att skapa en tydlig väg från problemförståelse till en fungerande produkt.',
        offer: [
          ['Analys och scope', 'Målgrupp, användningsfall, funktioner, beroenden och en genomförbar första version.'],
          ['UX/UI och prototyp', 'Informationsstruktur, flöden och gränssnitt som gör produkten begriplig innan onödig komplexitet byggs in.'],
          ['Utveckling', 'Mobilapp, webbapp och tillhörande backend eller integrationer när lösningen kräver det.'],
          ['Test och nästa version', 'Funktion, användbarhet och tekniska flöden granskas innan nästa prioritering görs. Driftsättning och vidareutveckling kan läggas till som separata steg.'],
        ],
        processTitle: 'Ett enkelt arbetssätt från idé till produkt',
        processIntro: 'Varje projekt ser olika ut, men besluten blir bättre när de tas i rätt ordning.',
        process: [
          ['Förstå', 'Vi definierar användaren, problemet, målet och de viktigaste begränsningarna.'],
          ['Avgränsa', 'Vi bestämmer vad som behöver byggas nu, vad som kan vänta och hur lösningen ska valideras.'],
          ['Bygg', 'Design och utveckling gör lösningen konkret så tidigt som möjligt.'],
          ['Testa och förbättra', 'Vi använder verkliga observationer och tekniska resultat för nästa iteration.'],
        ],
        principlesTitle: 'Principer för ett seriöst appprojekt',
        principles: [
          'Ingen teknik väljs bara för att den är trendig.',
          'En MVP ska vara avgränsad – inte ofärdig.',
          'Säkerhet, datalagring och integrationer bedöms utifrån den faktiska lösningen.',
          'Kostnad och omfattning blir tydligare när problemet först är strukturerat.',
        ],
        faqTitle: 'Vanliga frågor',
        faq: [
          ['Måste jag ha en färdig kravspecifikation?', 'Nej. Ett projekt kan börja med en idé eller ett problem. En del av arbetet kan vara att skapa den struktur som saknas.'],
          ['Bygger Keevo för både mobil och webb?', 'Ja, när projektets behov motiverar det. Val mellan mobilapp, webbapp eller en kombination görs efter målgrupp, funktion och tekniska krav.'],
          ['Kan man börja med en mindre MVP?', 'Ja, om en mindre första version går att använda för att testa de viktigaste antagandena utan att skapa onödig teknisk skuld.'],
        ],
        ctaTitle: 'Har du en idé som behöver bli konkret?',
        ctaBody: 'Beskriv vad du vill skapa, vem som ska använda det och vad du vill att produkten ska göra bättre.',
        ctaButton: 'Berätta om projektet',
        direct: 'Eller mejla direkt',
      },
      en: {
        eyebrow: 'Product development · App development',
        title: 'From app idea to a product people can actually use.',
        intro:
          'Keevo helps structure, design and develop mobile apps and web apps. A project can start with an idea, a defined problem or an existing concept that needs to become concrete.',
        home: 'Back to home',
        contact: 'Start a conversation',
        language: 'Svenska',
        situationsTitle: 'When app development is the right next step',
        situationsIntro:
          'We do not start with a technology choice. First we need to understand what the product should do, who it is for and which problem it should solve.',
        situations: [
          ['You have an app idea but no finished specification', 'We help define the users, needs, functions and what should be tested first.'],
          ['A manual workflow could become a digital product', 'We move from the operational problem to user flows and a solution that can be built and tested.'],
          ['You need a prototype or MVP to move forward', 'We prioritize what must work first and avoid building scope without a clear purpose.'],
        ],
        offerTitle: 'What Keevo can help with',
        offerIntro:
          'The scope is adapted to the project. The goal is a clear path from understanding the problem to a working product.',
        offer: [
          ['Analysis and scope', 'Target users, use cases, functions, dependencies and a feasible first version.'],
          ['UX/UI and prototype', 'Information structure, flows and interfaces that make the product understandable before unnecessary complexity is added.'],
          ['Development', 'Mobile app, web app and the backend or integrations the solution requires.'],
          ['Testing and next version', 'Functionality, usability and technical flows are reviewed before the next priorities are set. Deployment and continued development can be added as separate steps.'],
        ],
        processTitle: 'A practical path from idea to product',
        processIntro: 'Every project is different, but decisions improve when they are made in the right order.',
        process: [
          ['Understand', 'We define the user, problem, goal and the most important constraints.'],
          ['Scope', 'We decide what needs to be built now, what can wait and how the solution should be validated.'],
          ['Build', 'Design and development make the solution concrete as early as practical.'],
          ['Test and improve', 'Real observations and technical results guide the next iteration.'],
        ],
        principlesTitle: 'Principles for a serious app project',
        principles: [
          'Technology is not chosen simply because it is fashionable.',
          'An MVP should be focused – not unfinished.',
          'Security, data storage and integrations are assessed for the actual solution.',
          'Cost and scope become clearer once the problem has been structured.',
        ],
        faqTitle: 'Common questions',
        faq: [
          ['Do I need a finished specification?', 'No. A project can start with an idea or a problem. Part of the work can be creating the structure that is currently missing.'],
          ['Does Keevo build for both mobile and web?', 'Yes, when the project calls for it. The choice between mobile, web or a combination depends on users, functions and technical requirements.'],
          ['Can we start with a smaller MVP?', 'Yes, when a smaller first version can test the most important assumptions without creating unnecessary technical debt.'],
        ],
        ctaTitle: 'Have an idea that needs to become concrete?',
        ctaBody: 'Describe what you want to create, who should use it and what the product should make easier or better.',
        ctaButton: 'Tell us about the project',
        direct: 'Or email directly',
      },
    },
  },
  {
    key: 'digitalization',
    svPath: '/digitalisering/',
    enPath: '/en/digitization/',
    copy: {
      sv: {
        eyebrow: 'Digitalisering · Interna system',
        title: 'Gör manuella och splittrade arbetsflöden enklare att arbeta i.',
        intro:
          'Digitalisering börjar med att förstå arbetet som faktiskt görs. Keevo kartlägger processer, informationsflöden och återkommande administration innan vi föreslår ett internt system eller digitalt verktyg.',
        home: 'Till startsidan',
        contact: 'Beskriv problemet',
        language: 'English',
        situationsTitle: 'Vanliga signaler på att ett arbetssätt behöver ses över',
        situationsIntro:
          'Ett nytt system är inte alltid svaret. Först behöver vi skilja det som är nödvändigt från det som bara har blivit vana.',
        situations: [
          ['Samma information skrivs in flera gånger', 'Dubbla registreringar och manuella överföringar skapar tidsspill och ökar risken för fel.'],
          ['Information ligger utspridd', 'Kalkylark, mejl, dokument och separata system gör det svårt att få en gemensam bild.'],
          ['Återkommande administration tar oproportionerligt mycket tid', 'När många små manuella steg byggs på varandra blir processen svår att följa och förbättra.'],
        ],
        offerTitle: 'Från nuläge till ett enklare system',
        offerIntro:
          'Målet är inte att digitalisera varje steg. Målet är att ta bort onödig friktion och skapa ett arbetssätt som människor kan använda.',
        offer: [
          ['Processkartläggning', 'Vi går igenom roller, steg, information, beroenden och var friktionen faktiskt uppstår.'],
          ['Informationsstruktur', 'Vi definierar vad som behöver finnas, var det hör hemma och vilka användare som behöver tillgång.'],
          ['Internt verktyg eller system', 'Exempel kan vara adminflöden, dashboards, formulär, portaler eller andra verksamhetsnära gränssnitt.'],
          ['Integration och automation', 'När det finns tydlig nytta kan system kopplas ihop eller återkommande steg automatiseras.'],
        ],
        processTitle: 'Digitalisering utan att börja i verktyget',
        processIntro: 'Bra system kommer efter en begriplig process, inte före.',
        process: [
          ['Kartlägg', 'Vi dokumenterar nuläget och de faktiska arbetsmomenten.'],
          ['Förenkla', 'Onödiga steg, dubbelarbete och oklar ansvarsfördelning identifieras.'],
          ['Designa', 'Vi formar informationsmodell, användarflöden och systemgränser.'],
          ['Bygg och testa', 'Lösningen görs konkret och prövas mot verkliga arbetsflöden.'],
        ],
        principlesTitle: 'Vad vi försöker undvika',
        principles: [
          'Att digitalisera en dålig process exakt som den ser ut i dag.',
          'Att införa fler system än verksamheten behöver.',
          'Att göra enkla arbetsuppgifter beroende av onödig komplexitet.',
          'Att automatisera beslut som fortfarande behöver mänsklig bedömning.',
        ],
        faqTitle: 'Vanliga frågor',
        faq: [
          ['Måste vi veta vilket system vi behöver?', 'Nej. Det är ofta bättre att börja med problemet och nuläget. Lösningsformen bör komma efter analysen.'],
          ['Kan Keevo ersätta kalkylark och manuella listor?', 'I vissa fall. Först bedömer vi om ett eget verktyg faktiskt ger tillräcklig nytta jämfört med att förbättra det befintliga arbetssättet.'],
          ['Kan befintliga system kopplas ihop?', 'Det beror på deras API:er, behörigheter och tekniska begränsningar. Integrationsmöjligheter behöver därför kontrolleras i det specifika fallet.'],
        ],
        ctaTitle: 'Har ni ett arbetssätt som borde fungera bättre?',
        ctaBody: 'Beskriv processen som den ser ut i dag, vad som tar tid och var informationen fastnar.',
        ctaButton: 'Beskriv nuläget',
        direct: 'Eller mejla direkt',
      },
      en: {
        eyebrow: 'Digitization · Internal systems',
        title: 'Make manual and fragmented workflows easier to work with.',
        intro:
          'Digitization starts by understanding the work that is actually being done. Keevo maps processes, information flows and recurring administration before proposing an internal system or digital tool.',
        home: 'Back to home',
        contact: 'Describe the problem',
        language: 'Svenska',
        situationsTitle: 'Common signs that a workflow needs attention',
        situationsIntro:
          'A new system is not always the answer. First we separate what is necessary from what has simply become habit.',
        situations: [
          ['The same information is entered more than once', 'Duplicate entry and manual transfers waste time and increase the risk of errors.'],
          ['Information is spread across many places', 'Spreadsheets, email, documents and separate systems make it difficult to maintain a shared view.'],
          ['Recurring administration consumes too much time', 'Many small manual steps can accumulate into a process that is hard to follow and improve.'],
        ],
        offerTitle: 'From current state to a simpler system',
        offerIntro:
          'The goal is not to digitize every step. The goal is to remove unnecessary friction and create a workflow people can actually use.',
        offer: [
          ['Process mapping', 'We review roles, steps, information, dependencies and where friction actually occurs.'],
          ['Information structure', 'We define what needs to exist, where it belongs and which users need access.'],
          ['Internal tool or system', 'Examples can include admin flows, dashboards, forms, portals or other operational interfaces.'],
          ['Integration and automation', 'Where there is clear value, systems can be connected or recurring steps automated.'],
        ],
        processTitle: 'Digitization without starting with the tool',
        processIntro: 'A useful system follows a clear process, not the other way around.',
        process: [
          ['Map', 'We document the current state and the actual work being performed.'],
          ['Simplify', 'Unnecessary steps, duplicated work and unclear ownership are identified.'],
          ['Design', 'We shape the information model, user flows and system boundaries.'],
          ['Build and test', 'The solution is made concrete and tested against real workflows.'],
        ],
        principlesTitle: 'What we try to avoid',
        principles: [
          'Digitizing a poor process exactly as it exists today.',
          'Introducing more systems than the organization needs.',
          'Making simple tasks dependent on unnecessary complexity.',
          'Automating decisions that still require human judgement.',
        ],
        faqTitle: 'Common questions',
        faq: [
          ['Do we need to know which system we need?', 'No. It is often better to start with the problem and the current state. The solution should follow the analysis.'],
          ['Can Keevo replace spreadsheets and manual lists?', 'In some cases. We first assess whether a custom tool creates enough value compared with improving the existing workflow.'],
          ['Can existing systems be connected?', 'That depends on their APIs, permissions and technical limitations. Integration options must be assessed for the specific systems involved.'],
        ],
        ctaTitle: 'Do you have a workflow that should work better?',
        ctaBody: 'Describe how the process works today, what takes time and where information gets stuck.',
        ctaButton: 'Describe the current state',
        direct: 'Or email directly',
      },
    },
  },
  {
    key: 'ai',
    svPath: '/ai-automation/',
    enPath: '/en/ai-automation/',
    copy: {
      sv: {
        eyebrow: 'AI · Automation',
        title: 'Använd AI där den minskar arbete eller möjliggör bättre flöden.',
        intro:
          'Keevo utgår från arbetsuppgiften, datan och risken – inte från att AI måste vara lösningen. När nyttan är tydlig kan AI och automation byggas in i produkter, interna system eller återkommande arbetsflöden.',
        home: 'Till startsidan',
        contact: 'Diskutera ett arbetsflöde',
        language: 'English',
        situationsTitle: 'När AI eller automation kan vara relevant',
        situationsIntro:
          'Det finns störst värde när ett återkommande informationsarbete går att beskriva tydligt och resultatet kan kontrolleras.',
        situations: [
          ['Samma informationsarbete upprepas ofta', 'Exempel är sortering, sammanställning, textbearbetning eller överföring mellan tydliga steg.'],
          ['Människor behöver hitta och strukturera information snabbare', 'AI kan ibland hjälpa till att sammanfatta, klassificera eller föreslå nästa steg – med rätt kontrollnivå.'],
          ['En digital produkt behöver en AI-funktion', 'Vi bedömer om AI faktiskt förbättrar funktionen eller om traditionell logik är enklare och mer förutsägbar.'],
        ],
        offerTitle: 'Från användningsfall till kontrollerad lösning',
        offerIntro:
          'AI-projekt behöver tydliga gränser, testbara förväntningar och en plan för vad som händer när modellen har fel.',
        offer: [
          ['Lämplighetsbedömning', 'Vi definierar uppgiften, vilken data som finns, önskat resultat och var mänsklig kontroll behövs.'],
          ['Workflow-automation', 'Regelbaserade och AI-stödda steg kan kombineras när det minskar återkommande manuellt arbete.'],
          ['AI-integration', 'Modellfunktioner kan integreras i appar eller interna system när användningsfallet motiverar det.'],
          ['Kontroll och uppföljning', 'Felhantering, behörigheter, datagränser och kvalitetskontroll utformas efter lösningens risknivå.'],
        ],
        processTitle: 'Ett AI-projekt bör börja med ett mätbart användningsfall',
        processIntro: 'Det gör det möjligt att avgöra om lösningen faktiskt är bättre än dagens arbetssätt.',
        process: [
          ['Definiera uppgiften', 'Vi beskriver input, önskat output och vilka fel som inte är acceptabla.'],
          ['Bedöm data och risk', 'Datakällor, känslighet, behörigheter och behov av mänsklig granskning kartläggs.'],
          ['Prototypa', 'En begränsad lösning testas innan större integrationer byggs.'],
          ['Utvärdera', 'Kvalitet, tidsbesparing och felmönster används för beslut om nästa steg.'],
        ],
        principlesTitle: 'AI utan onödig hype',
        principles: [
          'AI används inte när vanlig programlogik är bättre.',
          'Mänsklig granskning behålls där konsekvensen av fel kräver det.',
          'Känslig data kräver en separat bedömning av lagring, leverantörer och åtkomst.',
          'Automatisering ska minska friktion – inte göra processen svårare att förstå.',
        ],
        faqTitle: 'Vanliga frågor',
        faq: [
          ['Måste ett automationsprojekt använda generativ AI?', 'Nej. Många bra automatiseringar bygger på vanlig logik, integrationer och tydliga regler. AI bör bara användas där den tillför något.'],
          ['Kan AI kopplas till ett befintligt system?', 'Ofta är det tekniskt möjligt, men det beror på systemets integrationsmöjligheter, dataflöden och säkerhetskrav.'],
          ['Hur hanteras risken för felaktiga AI-svar?', 'Genom avgränsade uppgifter, testdata, validering, fallback-logik och mänsklig kontroll där det behövs.'],
        ],
        ctaTitle: 'Finns det ett återkommande arbete ni vill minska?',
        ctaBody: 'Beskriv uppgiften, vilken information den använder och vad som i dag kräver mest manuell tid.',
        ctaButton: 'Beskriv arbetsflödet',
        direct: 'Eller mejla direkt',
      },
      en: {
        eyebrow: 'AI · Automation',
        title: 'Use AI where it reduces work or enables a better workflow.',
        intro:
          'Keevo starts with the task, the data and the risk – not with the assumption that AI must be the answer. When the value is clear, AI and automation can be built into products, internal systems or recurring workflows.',
        home: 'Back to home',
        contact: 'Discuss a workflow',
        language: 'Svenska',
        situationsTitle: 'When AI or automation may be relevant',
        situationsIntro:
          'The strongest use cases are recurring information tasks that can be clearly described and whose output can be checked.',
        situations: [
          ['The same information work is repeated frequently', 'Examples include sorting, summarizing, text processing or transferring information through defined steps.'],
          ['People need to find and structure information faster', 'AI can sometimes help summarize, classify or suggest next steps – with an appropriate level of control.'],
          ['A digital product needs an AI feature', 'We assess whether AI genuinely improves the function or whether traditional logic is simpler and more predictable.'],
        ],
        offerTitle: 'From use case to a controlled solution',
        offerIntro:
          'AI projects need clear boundaries, testable expectations and a plan for what happens when the model is wrong.',
        offer: [
          ['Suitability assessment', 'We define the task, available data, expected output and where human review is required.'],
          ['Workflow automation', 'Rule-based and AI-assisted steps can be combined when that reduces recurring manual work.'],
          ['AI integration', 'Model capabilities can be integrated into apps or internal systems when the use case justifies it.'],
          ['Control and follow-up', 'Error handling, permissions, data boundaries and quality controls are designed for the risk level of the solution.'],
        ],
        processTitle: 'An AI project should begin with a measurable use case',
        processIntro: 'That makes it possible to decide whether the solution is actually better than the current workflow.',
        process: [
          ['Define the task', 'We describe the input, expected output and which errors are not acceptable.'],
          ['Assess data and risk', 'Data sources, sensitivity, permissions and the need for human review are mapped.'],
          ['Prototype', 'A limited solution is tested before larger integrations are built.'],
          ['Evaluate', 'Quality, time saved and error patterns guide the decision about the next step.'],
        ],
        principlesTitle: 'AI without unnecessary hype',
        principles: [
          'AI is not used when conventional software logic is a better fit.',
          'Human review remains where the consequences of errors require it.',
          'Sensitive data requires a separate assessment of storage, providers and access.',
          'Automation should reduce friction – not make the process harder to understand.',
        ],
        faqTitle: 'Common questions',
        faq: [
          ['Does automation have to use generative AI?', 'No. Many useful automations rely on conventional logic, integrations and clear rules. AI should only be used where it adds value.'],
          ['Can AI be connected to an existing system?', 'It is often technically possible, but it depends on integration options, data flows and security requirements.'],
          ['How do you handle incorrect AI output?', 'Through scoped tasks, test data, validation, fallback logic and human review where required.'],
        ],
        ctaTitle: 'Is there recurring work you want to reduce?',
        ctaBody: 'Describe the task, the information it uses and what currently requires the most manual time.',
        ctaButton: 'Describe the workflow',
        direct: 'Or email directly',
      },
    },
  },
  {
    key: 'about',
    svPath: '/om-keevo/',
    enPath: '/en/about/',
    copy: {
      sv: {
        eyebrow: 'Om Keevo',
        title: 'En svensk digital produktstudio byggd för att göra idéer och problem konkreta.',
        intro:
          'Keevo kombinerar egna produkter med utvalda externa projekt inom produktutveckling, digitalisering och AI. Arbetssättet är praktiskt: förstå problemet, definiera nästa steg, bygg något som går att prova och förbättra därifrån.',
        home: 'Till startsidan',
        contact: 'Kontakta Keevo',
        language: 'English',
        situationsTitle: 'Vad Keevo är byggt för',
        situationsIntro:
          'Många digitala projekt börjar innan det finns en färdig kravspecifikation. Det kan finnas en idé, ett frustrerande arbetssätt eller en process som vuxit sig för komplex.',
        situations: [
          ['Idéer som behöver struktur', 'Vi hjälper till att göra mål, användare, funktioner och nästa beslut tydliga.'],
          ['Verksamhetsproblem som behöver förenklas', 'Vi börjar med att förstå processen innan ett system eller en automation föreslås.'],
          ['Produkter som behöver bli verkliga', 'Analys, UX/UI, utveckling och test kopplas ihop så att projektet kan gå från tanke till fungerande lösning.'],
        ],
        offerTitle: 'Tre kärnområden',
        offerIntro:
          'Keevo fokuserar på områden där produktförståelse och tekniskt genomförande behöver hänga ihop.',
        offer: [
          ['Produktutveckling', 'Mobilappar, webbappar, prototyper och MVP:er – från struktur och UX/UI till fungerande produkt.'],
          ['Digitalisering och interna system', 'Processer och informationsflöden förenklas innan verksamhetsnära system och verktyg byggs.'],
          ['AI och automation', 'AI och automatisering används där de faktiskt kan minska manuellt arbete eller möjliggöra en relevant funktion.'],
          ['Egna produkter', 'Keevo utvecklar också egna produkter, vilket gör produktarbete, testning och långsiktig förbättring till en del av den egna vardagen.'],
        ],
        processTitle: 'Arbetssättet',
        processIntro: 'Principen är enkel: förstå tillräckligt för att börja och förbättra med varje steg.',
        process: [
          ['Förstå', 'Problem, människor, processer och begränsningar sätts i sammanhang.'],
          ['Gör konkret', 'En prototyp, ett flöde eller en första fungerande version skapar något att bedöma.'],
          ['Testa', 'Teknik, funktion och användbarhet granskas mot verkliga behov.'],
          ['Förbättra', 'Det som lärs används för att prioritera nästa version.'],
        ],
        principlesTitle: 'Grundprinciper',
        principles: [
          'Problemförståelse före teknikval.',
          'Tydlig avgränsning före onödig omfattning.',
          'AI används som verktyg – inte som etikett.',
          'Lösningar ska fungera utanför presentationen.',
        ],
        faqTitle: 'Kort om Keevo',
        faq: [
          ['Var finns Keevo?', 'Keevo har sin bas i Sverige och kan arbeta med svenska och internationella projekt.'],
          ['Vem grundade Keevo?', 'Keevo grundades av Kevan Issa.'],
          ['Vilka typer av kunder kan kontakta Keevo?', 'Både personer och organisationer kan börja med en idé eller ett problem. Omfattning och lämplighet bedöms innan ett projekt startar.'],
        ],
        ctaTitle: 'Har du något som behöver bli tydligare, enklare eller mer konkret?',
        ctaBody: 'Du behöver inte börja med rätt tekniska ord. Beskriv idén eller problemet som du ser det i dag.',
        ctaButton: 'Starta en dialog',
        direct: 'Eller mejla direkt',
      },
      en: {
        eyebrow: 'About Keevo',
        title: 'A Swedish digital product studio built to make ideas and problems concrete.',
        intro:
          'Keevo combines its own products with selected external projects in product development, digitization and AI. The approach is practical: understand the problem, define the next step, build something that can be tested and improve from there.',
        home: 'Back to home',
        contact: 'Contact Keevo',
        language: 'Svenska',
        situationsTitle: 'What Keevo is built for',
        situationsIntro:
          'Many digital projects begin before a finished specification exists. There may be an idea, a frustrating workflow or a process that has become too complex.',
        situations: [
          ['Ideas that need structure', 'We help make goals, users, functions and the next decisions clear.'],
          ['Operational problems that need simplification', 'We start by understanding the process before proposing a system or automation.'],
          ['Products that need to become real', 'Analysis, UX/UI, development and testing are connected so a project can move from thought to working solution.'],
        ],
        offerTitle: 'Three core areas',
        offerIntro:
          'Keevo focuses on areas where product understanding and technical execution need to work together.',
        offer: [
          ['Product development', 'Mobile apps, web apps, prototypes and MVPs – from structure and UX/UI to a working product.'],
          ['Digitization and internal systems', 'Processes and information flows are simplified before operational systems and tools are built.'],
          ['AI and automation', 'AI and automation are used where they can genuinely reduce manual work or enable a relevant function.'],
          ['Own products', 'Keevo also develops its own products, making product work, testing and long-term improvement part of the studio’s own practice.'],
        ],
        processTitle: 'The approach',
        processIntro: 'The principle is simple: understand enough to begin and improve with every step.',
        process: [
          ['Understand', 'Problems, people, processes and constraints are put into context.'],
          ['Make it concrete', 'A prototype, flow or first working version creates something that can be evaluated.'],
          ['Test', 'Technology, function and usability are reviewed against real needs.'],
          ['Improve', 'What is learned is used to prioritize the next version.'],
        ],
        principlesTitle: 'Core principles',
        principles: [
          'Understand the problem before choosing technology.',
          'Clear scope before unnecessary breadth.',
          'AI is used as a tool – not as a label.',
          'Solutions should work outside the presentation.',
        ],
        faqTitle: 'About Keevo',
        faq: [
          ['Where is Keevo based?', 'Keevo is based in Sweden and can work with Swedish and international projects.'],
          ['Who founded Keevo?', 'Keevo was founded by Kevan Issa.'],
          ['Who can contact Keevo?', 'Both individuals and organizations can start with an idea or a problem. Scope and fit are assessed before a project begins.'],
        ],
        ctaTitle: 'Have something that needs to become clearer, simpler or more concrete?',
        ctaBody: 'You do not need to start with the right technical vocabulary. Describe the idea or problem as you see it today.',
        ctaButton: 'Start a conversation',
        direct: 'Or email directly',
      },
    },
  },
];

function resolvePage() {
  const pathname = window.location.pathname;
  const locale: Locale = pathname.startsWith('/en/') ? 'en' : 'sv';
  const page = pageConfigs.find((candidate) =>
    pathname === candidate.svPath ||
    pathname === candidate.svPath.slice(0, -1) ||
    pathname === candidate.enPath ||
    pathname === candidate.enPath.slice(0, -1)
  );

  return { page: page ?? pageConfigs[0], locale };
}

function getVisual(key: PageKey, locale: Locale) {
  if (key === 'app') return <ProductDevelopmentVisual locale={locale} />;
  if (key === 'digitalization') return <DigitizationVisual locale={locale} />;
  if (key === 'ai') return <AutomationVisual locale={locale} />;

  return (
    <div className="relative mx-auto w-full max-w-[520px]">
      <div className="absolute inset-10 rounded-[42px] bg-[#6F61FF]/15 blur-3xl" aria-hidden />
      <div className="relative overflow-hidden rounded-[38px] border border-[#DFDCE8] bg-white p-3 shadow-[0_30px_80px_rgba(31,28,55,0.12)]">
        <img
          src="/kevan-issa-founder-editorial.png"
          alt="Kevan Issa"
          className="aspect-[4/5] w-full rounded-[30px] object-cover"
        />
      </div>
    </div>
  );
}

export default function SeoLandingPage() {
  const { page, locale } = resolvePage();
  const t = page.copy[locale];
  const homePath = locale === 'en' ? '/en/' : '/';

  useEffect(() => {
    document.documentElement.lang = locale === 'sv' ? 'sv-SE' : 'en';
  }, [locale]);

  const subject = locale === 'sv'
    ? `Keevo – förfrågan om ${t.eyebrow}`
    : `Keevo – enquiry about ${t.eyebrow}`;

  const changeLocale = (nextLocale: Locale) => {
    if (nextLocale === locale) return;
    window.location.assign(nextLocale === 'en' ? page.enPath : page.svPath);
  };

  return (
    <div className="min-h-screen bg-[#F4F3EF] text-[#12141A]">
      <Navigation locale={locale} onLocaleChange={changeLocale} />

      <main>
        <section className="relative overflow-hidden px-6 pb-24 pt-36 md:px-8 md:pb-28 md:pt-40 lg:px-16 lg:pb-32 lg:pt-44">
          <div className="pointer-events-none absolute inset-0" aria-hidden>
            <div className="absolute left-[8%] top-12 h-72 w-72 rounded-full bg-[#7568FF]/12 blur-3xl" />
            <div className="absolute right-[8%] top-40 h-96 w-96 rounded-full bg-[#BDAFFF]/16 blur-3xl" />
          </div>
          <div className="relative mx-auto grid max-w-[1450px] items-center gap-14 lg:grid-cols-[1.05fr_.95fr] lg:gap-16">
            <div>
              <a href={homePath} className="mb-8 inline-flex items-center gap-2 text-sm font-semibold text-[#666973] transition hover:text-[#5B4BFF]">
                <ArrowLeft size={16} /> {t.home}
              </a>
              <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-[#DCD8EF] bg-white/75 px-4 py-2 text-xs font-bold uppercase tracking-[0.12em] text-[#5B4BFF]">
                <Sparkles size={15} aria-hidden /> {t.eyebrow}
              </div>
              <h1 className="max-w-[880px] font-sora text-[48px] font-extrabold leading-[1.04] tracking-[-0.05em] md:text-[64px] lg:text-[74px]">
                {t.title}
              </h1>
              <p className="mt-7 max-w-[760px] text-lg leading-8 text-[#565A63] md:text-xl">
                {t.intro}
              </p>
              <div className="mt-9 flex flex-wrap gap-3">
                <a
                  href={`mailto:${siteConfig.email}?subject=${encodeURIComponent(subject)}`}
                  className="inline-flex min-h-12 items-center gap-2 rounded-full bg-[#5B4BFF] px-6 font-bold text-white transition hover:bg-[#4B37E0]"
                >
                  {t.contact} <ArrowRight size={18} />
                </a>
                <span className="inline-flex min-h-12 items-center rounded-full border border-[#DCD9E2] bg-white/70 px-5 text-sm font-semibold text-[#62656E]">
                  {locale === 'sv' ? 'Sverige · Worldwide' : 'Sweden · Worldwide'}
                </span>
              </div>
            </div>

            <div className="relative min-h-[420px]">{getVisual(page.key, locale)}</div>
          </div>
        </section>

        <section className="border-y border-[#E8E5DE] bg-white/65 px-6 py-24 md:px-8 lg:px-16">
          <div className="mx-auto max-w-[1250px]">
            <div className="max-w-[800px]">
              <div className="section-eyebrow"><span />{t.situationsTitle}</div>
              <p className="mt-5 text-lg leading-8 text-[#62656E]">{t.situationsIntro}</p>
            </div>
            <div className="mt-12 grid gap-5 md:grid-cols-3">
              {t.situations.map(([title, body], index) => {
                const Icon = index === 0 ? Lightbulb : index === 1 ? Workflow : Search;
                return (
                  <article key={title} className="rounded-[28px] border border-[#E4E1DA] bg-[#FAF9F6] p-7">
                    <div className="mb-6 grid h-12 w-12 place-items-center rounded-2xl bg-[#EEEAFE] text-[#5B4BFF]">
                      <Icon size={22} aria-hidden />
                    </div>
                    <h2 className="font-sora text-xl font-bold tracking-[-0.02em]">{title}</h2>
                    <p className="mt-3 leading-7 text-[#646771]">{body}</p>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        <section className="px-6 py-24 md:px-8 lg:px-16 lg:py-32">
          <div className="mx-auto max-w-[1250px]">
            <div className="max-w-[840px]">
              <div className="section-eyebrow"><span />{t.offerTitle}</div>
              <p className="mt-5 text-lg leading-8 text-[#62656E]">{t.offerIntro}</p>
            </div>
            <div className="mt-12 grid gap-5 md:grid-cols-2">
              {t.offer.map(([title, body], index) => {
                const Icon = page.key === 'app'
                  ? Smartphone
                  : page.key === 'digitalization'
                    ? Network
                    : page.key === 'ai'
                      ? Bot
                      : index === 0
                        ? Smartphone
                        : index === 1
                          ? Network
                          : index === 2
                            ? Bot
                            : Sparkles;
                return (
                  <article key={title} className="rounded-[30px] border border-[#E0DDE7] bg-white p-8 shadow-[0_18px_50px_rgba(31,28,55,0.045)]">
                    <div className="mb-6 flex items-center gap-4">
                      <div className="grid h-12 w-12 place-items-center rounded-2xl bg-[#5B4BFF] text-white">
                        <Icon size={22} aria-hidden />
                      </div>
                      <span className="font-mono text-xs font-bold tracking-[0.12em] text-[#85818F]">0{index + 1}</span>
                    </div>
                    <h2 className="font-sora text-2xl font-bold tracking-[-0.025em]">{title}</h2>
                    <p className="mt-4 leading-7 text-[#646771]">{body}</p>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        <section className="bg-[#171820] px-6 py-24 text-white md:px-8 lg:px-16 lg:py-32">
          <div className="mx-auto max-w-[1250px]">
            <div className="max-w-[820px]">
              <div className="text-xs font-bold uppercase tracking-[0.14em] text-[#AFA5FF]">{t.processTitle}</div>
              <p className="mt-5 text-lg leading-8 text-[#BFC1C9]">{t.processIntro}</p>
            </div>
            <div className="mt-12 grid gap-4 md:grid-cols-4">
              {t.process.map(([title, body], index) => (
                <article key={title} className="rounded-[26px] border border-white/10 bg-white/[0.035] p-6">
                  <span className="font-mono text-xs font-bold text-[#AFA5FF]">0{index + 1}</span>
                  <h2 className="mt-5 font-sora text-xl font-bold">{title}</h2>
                  <p className="mt-3 leading-7 text-[#B8BAC3]">{body}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {page.key === 'about' && (
          <section className="bg-white px-6 py-24 md:px-8 lg:px-16 lg:py-32">
            <div className="mx-auto grid max-w-[1150px] items-center gap-12 lg:grid-cols-[.8fr_1.2fr]">
              <div className="overflow-hidden rounded-[34px] border border-[#E2DFE7] bg-[#F4F3EF] p-3">
                <img src="/kevan-issa-founder-editorial.png" alt="Kevan Issa" className="w-full rounded-[27px] object-cover" />
              </div>
              <div>
                <div className="section-eyebrow"><span />{locale === 'sv' ? 'Grundare' : 'Founder'}</div>
                <h2 className="mt-5 font-sora text-4xl font-bold tracking-[-0.04em] md:text-5xl">Kevan Issa</h2>
                <p className="mt-3 font-semibold text-[#5B4BFF]">
                  {locale === 'sv'
                    ? 'Grundare · System/App- och effektiviseringskonsult'
                    : 'Founder · Systems, App & Process Improvement Consultant'}
                </p>
                <p className="mt-6 max-w-[720px] text-lg leading-8 text-[#62656E]">
                  {locale === 'sv'
                    ? 'Keevos arbetssätt bygger på att koppla ihop problemförståelse, produktbeslut och praktiskt genomförande. Fokus ligger på att göra nästa steg konkret och att låta fungerande prototyper och system skapa bättre beslutsunderlag än långa antaganden.'
                    : 'Keevo’s approach connects problem understanding, product decisions and practical execution. The focus is on making the next step concrete and using working prototypes and systems to create better evidence than long chains of assumptions.'}
                </p>
              </div>
            </div>
          </section>
        )}

        <section className="px-6 py-24 md:px-8 lg:px-16 lg:py-32">
          <div className="mx-auto grid max-w-[1250px] gap-8 lg:grid-cols-[.8fr_1.2fr]">
            <div>
              <div className="section-eyebrow"><span />{t.principlesTitle}</div>
              <div className="mt-8 space-y-3">
                {t.principles.map((principle) => (
                  <div key={principle} className="flex items-start gap-3 rounded-2xl border border-[#E1DEE6] bg-white/70 px-5 py-4">
                    <Check className="mt-0.5 shrink-0 text-[#5B4BFF]" size={18} aria-hidden />
                    <span className="font-semibold leading-6 text-[#4E515A]">{principle}</span>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div className="section-eyebrow"><span />{t.faqTitle}</div>
              <div className="mt-8 divide-y divide-[#E1DEE6] rounded-[28px] border border-[#E1DEE6] bg-white">
                {t.faq.map(([question, answer]) => (
                  <article key={question} className="p-6 md:p-7">
                    <h2 className="font-sora text-lg font-bold tracking-[-0.015em]">{question}</h2>
                    <p className="mt-3 leading-7 text-[#646771]">{answer}</p>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="px-6 pb-28 md:px-8 lg:px-16 lg:pb-36">
          <div className="mx-auto max-w-[1100px] rounded-[38px] bg-[#5B4BFF] px-7 py-12 text-center text-white shadow-[0_30px_80px_rgba(91,75,255,0.2)] md:px-12 md:py-16">
            <ShieldCheck className="mx-auto text-[#D9D4FF]" size={30} aria-hidden />
            <h2 className="mx-auto mt-5 max-w-[800px] font-sora text-4xl font-bold leading-tight tracking-[-0.04em] md:text-5xl">{t.ctaTitle}</h2>
            <p className="mx-auto mt-5 max-w-[700px] text-lg leading-8 text-[#E8E6FF]">{t.ctaBody}</p>
            <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
              <a
                href={`mailto:${siteConfig.email}?subject=${encodeURIComponent(subject)}`}
                className="inline-flex min-h-12 items-center gap-2 rounded-full bg-white px-6 font-bold text-[#4B37E0] transition hover:bg-[#F3F1FF]"
              >
                {t.ctaButton} <ArrowRight size={18} />
              </a>
              <span className="text-sm text-[#E4E1FF]">{t.direct} · {siteConfig.email}</span>
            </div>
          </div>
        </section>
      </main>

      <SiteFooter locale={locale} />
    </div>
  );
}
